import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import type { Branch, Identity } from '../../lib/types'
import { Chrome } from '../../ui/Chrome'
import { useSession } from '../../ui/Session'
import { useStaffText } from '../../ui/StaffText'
import { Notice } from '../kit'
import { useReportToasts } from '../Toasts'
import { Builder } from './Builder'
import { ReportsList } from './ReportsList'
import { BookingsPage, BreaksPage, EstimatesPage, TicketsPage } from './ClassicPages'
import '../../styles/reports.css'

export type ToastFn = (text: string, kind?: 'error') => void

/** What every Reports screen shares: the reader, the branches they reach, navigation and toasts. */
export interface ReportsCtx {
  identity: Identity
  branches: Branch[] | null
  go: (to: string) => void
  toast: ToastFn
  /** The organisation the installation owner reads reports of; undefined for everyone else. */
  organisation?: string
}

/** /reports and everything under it: the list, the builder, and the Tickets and Bookings pages. */
export function Reports({ identity, onNavigate, path = '/reports' }: { identity: Identity; onNavigate: (to: string) => void; path?: string }) {
  const { say } = useStaffText()
  const session = useSession()
  const [branches, setBranches] = useState<Branch[] | null>(null)
  const allowed = identity.permissions.includes('report.read')
  // The owner reads the organisation chosen in the top bar (App opens this page only once there is one).
  const org = session?.org
  const organisation = org?.organisation || (org?.organisations.length === 1 ? org.organisations[0]!.id : undefined)

  useEffect(() => {
    if (!allowed) return
    api
      .branches()
      .then((r) => setBranches(organisation ? r.branches.filter((b) => b.tenantId === organisation) : r.branches))
      .catch(() => setBranches([]))
  }, [allowed, organisation])

  const toast: ToastFn = useReportToasts()

  if (!allowed) {
    return (
      <Chrome title={say('entry.app.reports')}>
        <main className="page">
          <Notice>{say('errors.FORBIDDEN')}</Notice>
        </main>
      </Chrome>
    )
  }

  const go = session?.navigate ?? onNavigate
  const ctx: ReportsCtx = { identity, branches, go, toast, organisation }
  const rest = path.replace(/^\/reports\/?/, '')

  let page
  if (rest === '') page = <ReportsList ctx={ctx} />
  else if (rest === 'tickets') page = <TicketsPage ctx={ctx} />
  else if (rest === 'bookings') page = <BookingsPage ctx={ctx} />
  else if (rest === 'estimates') page = <EstimatesPage ctx={ctx} />
  else if (rest === 'breaks') page = <BreaksPage ctx={ctx} />
  else if (rest.startsWith('builtin/')) page = <Builder key={rest} ctx={ctx} builtInId={rest.slice('builtin/'.length)} />
  else page = <Builder key={rest} ctx={ctx} reportId={rest} />

  return (
    <Chrome title={say('entry.app.reports')}>
      {page}
    </Chrome>
  )
}
